/**
 * /admin/audit — read-only browser for `app.audit_event`.
 *
 * Supports the operational use case "show me what happened, scoped by
 * action / actor / time" plus the compliance use case "prove the chain
 * is intact" via a Verify button (CLAUDE.md §9.3). The page is
 * developer-and-above (`audit.read`); the policy layer denies plain
 * users before this component runs.
 *
 * Rows render newest-first. Each row has an expandable detail panel
 * showing `before`/`after` JSON, `request_id`, `event_hash` — the
 * defensible-evidence fields an auditor walks. Nothing in this page
 * mutates the audit table or its rows.
 */

import { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Chip,
  Code,
  Container,
  Group,
  Loader,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconChevronLeft,
  IconChevronRight,
  IconDownload,
  IconRefresh,
  IconShieldCheck,
} from "@tabler/icons-react";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { useMe } from "@/features/users/useMe";
import { toastApiError, toastError, toastSuccess } from "@/shared/ui/toast";
import {
  type AuditEvent,
  downloadAuditTrail,
  listAuditEvents,
  verifyAuditChain,
  type VerifyResult,
} from "@/shared/api/audit";

const PAGE_SIZE = 25;

// Known audit actions, for the filter dropdown. Keep in sync with the backend
// audit emissions — `grep -ro 'action="[^"]*"' backend/src` is the source of
// truth. Hardcoded rather than enum-fetched because the vocabulary is small and
// this is a sysadmin tool; a stale entry is harmless (the dropdown falls back to
// "All", and the action column still renders whatever a row actually carries).
const KNOWN_ACTIONS = [
  "user.provisioned",
  "user.profile_synced",
  "user.profile_updated",
  "user.deactivated",
  "user.reactivated",
  "user.offboarded",
  "user.erased",
  "user.data_exported",
  "user.role_assigned",
  "user.role_revoked",
  "user.grant_created",
  "user.grant_revoked",
  "auth.login_succeeded",
  "auth.login_denied",
  "auth.token_invalid",
  "authz.denied",
  "audit.export_run",
  "audit.verify_run",
  "tenant.pii_key_rotated",
  "tenant.erasure_run",
  "system.ready_failed",
  // Phase 2 — integration connector console.
  "connection.created",
  "connection.updated",
  "connection.credential.updated",
  "connection.tested",
  "connection.deleted",
  "document.downloaded",
  "document.bulk_downloaded",
  "attachment.downloaded",
  "audit_trail.downloaded",
  "file.uploaded",
  "file.downloaded",
  "file.bulk_downloaded",
  "file.deleted",
  "container.created",
  "container.deleted",
  "audit.exported",
  // Database connector — rows leaving a customer's database are an access
  // event, the same as a blob download (ADR-0022).
  "database.rows_read",
  // Phase 2 — Veeva file staging (the vault's "FTP" area).
  "veeva_file.downloaded",
  "veeva_file.bulk_downloaded",
  "veeva_file.uploaded",
  "veeva_file.folder_created",
  "veeva_file.deleted",
  "veeva_file.renamed",
  // Phase 3 — projects + pipelines (orchestration).
  "project.created",
  "pipeline.created",
  "pipeline.version_saved",
  "pipeline.run",
  // Phase 3 — pipeline targets that write to external systems.
  "object.records_created",
  "object.records_updated",
  "object.records_upserted",
  "document.records_created",
  "document.metadata_updated",
  "document.state_changed",
  "document.reclassified",
  // Phase 3 — AI. The rows were being written before these were listed here,
  // which made them invisible in this filter and look absent.
  "ai.inference",
  "chat.created",
  "chat.renamed",
  "chat.model_changed",
  "chat.indexes_changed",
  "chat.deleted",
  "risk_assessment.created",
  "risk_assessment.exported",
  "risk_assessment.deleted",
  // ADR-0015 — owner-scoped visibility. `resource.admin_read` is the row an
  // Administrator's bypass owes: it marks reading a resource they neither own
  // nor were granted, and it is the one an auditor looks for.
  "resource.admin_read",
  "resource.access_granted",
  "resource.access_revoked",
  // Document indexes ("collections"). Shares ride resource.access_* above.
  "document_index.created",
  "document_index.renamed",
  "document_index.documents_added",
  "document_index.documents_removed",
  "document_index.build_started",
  "document_index.build_completed",
  "document_index.deleted",
  "document_index.document_read",
];

// Operator quick-filter chips. Each chip is a shortcut for the most
// common admin views — clicking sets the action filter to the chip's
// value; clicking again (or "Show all") clears it. The dropdown still
// drives arbitrary actions; chips are a UX speed-up, not a replacement.
// Ordered by the slice that landed each action so the row reads as a
// timeline of the platform's compliance surface.
const OPERATOR_CHIPS: { label: string; action: string; color: string }[] = [
  // Slice 6.3 / 6.4 — the audit-export + chain-verifier emissions an
  // on-call operator polls weekly per docs/runbooks/audit-export-verify.md.
  { label: "Audit exports", action: "audit.export_run", color: "teal" },
  { label: "Audit verifies", action: "audit.verify_run", color: "teal" },
  // Slice 6.5 — erasure runner outcomes. Distinct from offboard (which
  // queues the erasure); these are the actual KEK-destroy events that
  // are GDPR §17 evidence (docs/runbooks/data-erasure.md).
  { label: "Erasures", action: "user.erased", color: "red" },
  // Slice 3.1 — periodic KEK rotation (runbooks/rotate-pii-keys.md).
  { label: "PII rotations", action: "tenant.pii_key_rotated", color: "orange" },
];

export function AuditViewer() {
  const getToken = useAccessToken();
  const { data: me } = useMe();
  const isAdmin = me?.roles.includes("administrator") ?? false;
  const [exporting, setExporting] = useState(false);
  const [items, setItems] = useState<AuditEvent[]>([]);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionFilter, setActionFilter] = useState<string | null>(null);
  const [actorFilter, setActorFilter] = useState("");
  const [actorEmailFilter, setActorEmailFilter] = useState("");
  const [verify, setVerify] = useState<VerifyResult | null>(null);
  const [verifying, setVerifying] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  const load = useCallback(
    async (cursor: string | null) => {
      setLoading(true);
      setError(null);
      try {
        const token = await getToken();
        const page = await listAuditEvents(token, {
          limit: PAGE_SIZE,
          cursor,
          action: actionFilter,
          actor_user_id: actorFilter.trim() || null,
          actor_email: actorEmailFilter.trim() || null,
        });
        setItems(page.items);
        setNextCursor(page.next_cursor);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      } finally {
        setLoading(false);
      }
    },
    [getToken, actionFilter, actorFilter, actorEmailFilter],
  );

  useEffect(() => {
    setCursorHistory([]);
    void load(null);
  }, [load]);

  const onNext = () => {
    if (!nextCursor) return;
    setCursorHistory((h) => [...h, nextCursor]);
    void load(nextCursor);
  };
  const onPrev = () => {
    if (cursorHistory.length === 0) return;
    const next = cursorHistory.slice(0, -1);
    setCursorHistory(next);
    const cursor = next.length === 0 ? null : (next[next.length - 1] ?? null);
    void load(cursor);
  };

  const onExport = async () => {
    setExporting(true);
    try {
      const token = await getToken();
      await downloadAuditTrail(token, {
        action: actionFilter,
        actor_user_id: actorFilter.trim() || null,
        actor_email: actorEmailFilter.trim() || null,
      });
      toastSuccess("Audit trail downloaded", "Saved as audit-trail.csv");
    } catch (err) {
      toastApiError("Download failed", err);
    } finally {
      setExporting(false);
    }
  };

  const onVerify = async () => {
    setVerifying(true);
    try {
      const token = await getToken();
      const result = await verifyAuditChain(token);
      setVerify(result);
      if (result.ok) {
        toastSuccess("Chain verified", `Walked ${result.events_checked} events. Hashes reconcile.`);
      } else {
        toastError(
          "Chain BROKEN",
          `First bad event: ${result.first_bad_event_id ?? "?"} — ${result.reason ?? ""}`,
          { autoClose: false },
        );
      }
    } catch (err) {
      toastApiError("Verify failed", err);
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Container size="xl" py="xl">
      <Group justify="space-between" align="end" mb="md" wrap="wrap">
        <Stack gap={2}>
          <Title order={2}>Audit log</Title>
        </Stack>
        <Group gap="xs">
          {isAdmin && (
            <Tooltip label="Download the audit trail as CSV (honours the current filters).">
              <Button
                variant="light"
                leftSection={<IconDownload size={16} />}
                onClick={() => void onExport()}
                loading={exporting}
                aria-label="Download audit trail"
              >
                Download
              </Button>
            </Tooltip>
          )}
          <Tooltip label="Recompute the hash chain end-to-end for this tenant.">
            <Button
              variant="light"
              leftSection={<IconShieldCheck size={16} />}
              onClick={() => void onVerify()}
              loading={verifying}
              aria-label="Verify audit chain"
            >
              Verify chain
            </Button>
          </Tooltip>
          <Tooltip label="Reload">
            <Button
              variant="subtle"
              leftSection={<IconRefresh size={16} />}
              onClick={() => void load(null)}
              aria-label="Reload audit events"
            >
              Reload
            </Button>
          </Tooltip>
        </Group>
      </Group>

      {verify && !verify.ok && (
        <Alert color="red" variant="filled" title="Hash chain broken" mb="md">
          <Text size="sm">
            First bad event: <Code>{verify.first_bad_event_id ?? "—"}</Code>. {verify.reason}
          </Text>
        </Alert>
      )}

      <Stack gap="xs" mb="md">
        <Group gap="xs" wrap="wrap" aria-label="Quick filters by admin operation">
          <Text size="xs" c="dimmed" tt="uppercase" fw={500} mr="xs">
            Quick filters
          </Text>
          {OPERATOR_CHIPS.map((chip) => (
            <Chip
              key={chip.action}
              size="sm"
              color={chip.color}
              variant="light"
              checked={actionFilter === chip.action}
              onChange={(checked) => setActionFilter(checked ? chip.action : null)}
              aria-label={`Quick filter: ${chip.label}`}
              aria-pressed={actionFilter === chip.action}
            >
              {chip.label}
            </Chip>
          ))}
          {actionFilter !== null && (
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={() => setActionFilter(null)}
              aria-label="Clear quick filter"
            >
              Show all
            </Button>
          )}
        </Group>
        <Group gap="md" wrap="wrap" align="end">
          <Select
            label="Action"
            placeholder="All actions"
            clearable
            data={KNOWN_ACTIONS}
            value={actionFilter}
            onChange={setActionFilter}
            aria-label="Filter by action"
            style={{ minWidth: 240 }}
          />
          <TextInput
            label="Actor email"
            placeholder="Search by user email (contains)"
            value={actorEmailFilter}
            onChange={(e) => setActorEmailFilter(e.currentTarget.value)}
            aria-label="Filter by actor email"
            style={{ flex: "1 1 260px" }}
          />
          <TextInput
            label="Actor user ID"
            placeholder="ULID — filters to one user's events"
            value={actorFilter}
            onChange={(e) => setActorFilter(e.currentTarget.value)}
            aria-label="Filter by actor user ID"
            style={{ flex: "1 1 260px" }}
          />
        </Group>
      </Stack>

      {error && (
        <Alert color="red" variant="light" title="Could not load audit log" mb="md">
          {error}
        </Alert>
      )}

      {loading ? (
        <Group justify="center" py="xl">
          <Loader />
        </Group>
      ) : items.length === 0 ? (
        <Text c="dimmed">No matching events.</Text>
      ) : (
        <Table withTableBorder withColumnBorders striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Time (UTC)</Table.Th>
              <Table.Th>Action</Table.Th>
              <Table.Th>Resource</Table.Th>
              <Table.Th>Actor</Table.Th>
              <Table.Th>Reason</Table.Th>
              <Table.Th>Retention</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {items.map((row) => (
              <AuditRow
                key={row.event_id}
                row={row}
                expanded={expanded === row.event_id}
                onToggle={() =>
                  setExpanded((prev) => (prev === row.event_id ? null : row.event_id))
                }
              />
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Group justify="end" gap="xs" mt="md">
        <Button
          variant="default"
          leftSection={<IconChevronLeft size={14} />}
          onClick={onPrev}
          disabled={cursorHistory.length === 0 || loading}
        >
          Previous
        </Button>
        <Button
          variant="default"
          rightSection={<IconChevronRight size={14} />}
          onClick={onNext}
          disabled={!nextCursor || loading}
        >
          Next
        </Button>
      </Group>
    </Container>
  );
}

function AuditRow({
  row,
  expanded,
  onToggle,
}: {
  row: AuditEvent;
  expanded: boolean;
  onToggle: () => void;
}) {
  return (
    <>
      <Table.Tr onClick={onToggle} style={{ cursor: "pointer" }} aria-expanded={expanded}>
        <Table.Td>
          <Code>{row.event_time_utc}</Code>
        </Table.Td>
        <Table.Td>
          <Badge color={actionColor(row.action)} variant="light">
            {row.action}
          </Badge>
        </Table.Td>
        <Table.Td>
          <Text size="sm" ff="monospace">
            {row.resource_type}/{row.resource_id || "—"}
          </Text>
        </Table.Td>
        <Table.Td>
          {row.actor_email || row.actor_display_name ? (
            <Stack gap={0}>
              <Text size="sm">{row.actor_email ?? row.actor_display_name}</Text>
              <Text size="xs" c="dimmed" ff="monospace">
                {row.actor_user_id ?? "—"}
              </Text>
            </Stack>
          ) : (
            <Text size="sm" ff="monospace">
              {row.actor_user_id ?? "—"}
            </Text>
          )}
        </Table.Td>
        <Table.Td>
          <Text size="sm" lineClamp={1}>
            {row.reason ?? "—"}
          </Text>
        </Table.Td>
        <Table.Td>
          <Badge color="gray" variant="outline">
            {row.retention_class}
          </Badge>
        </Table.Td>
      </Table.Tr>
      {expanded && (
        <Table.Tr>
          <Table.Td colSpan={6}>
            <Stack gap="xs" p="sm">
              <Field label="event_id" value={row.event_id} />
              <Field
                label="actor"
                value={`${row.actor_email ?? row.actor_display_name ?? "—"} (${row.actor_user_id ?? "—"})`}
              />
              <Field label="request_id" value={row.request_id} />
              <Field
                label="source_ip / user_agent"
                value={`${row.source_ip ?? "—"} / ${row.user_agent ?? "—"}`}
              />
              <Field label="prev_hash" value={row.prev_hash_hex} mono />
              <Field label="event_hash" value={row.event_hash_hex} mono />
              <Field
                label="before"
                value={row.before ? JSON.stringify(row.before, null, 2) : "—"}
                pre
              />
              <Field
                label="after"
                value={row.after ? JSON.stringify(row.after, null, 2) : "—"}
                pre
              />
            </Stack>
          </Table.Td>
        </Table.Tr>
      )}
    </>
  );
}

function Field({
  label,
  value,
  mono,
  pre,
}: {
  label: string;
  value: string;
  mono?: boolean;
  pre?: boolean;
}) {
  return (
    <div>
      <Text size="xs" c="dimmed" tt="uppercase" fw={500}>
        {label}
      </Text>
      {pre ? (
        <Code block>{value}</Code>
      ) : (
        <Text size="sm" ff={mono ? "monospace" : undefined} style={{ wordBreak: "break-all" }}>
          {value}
        </Text>
      )}
    </div>
  );
}

function actionColor(action: string): string {
  // Order matters: more-specific prefixes first.
  if (action === "user.erased") return "red"; // destructive, GDPR §17
  if (action.startsWith("authz.")) return "red";
  if (action.startsWith("auth.")) return "blue";
  if (action.startsWith("system.")) return "yellow";
  if (action.startsWith("audit.")) return "teal"; // export_run, verify_run
  if (action.startsWith("tenant.")) return "orange"; // pii_key_rotated, erasure_run
  // Phase 2 — integration connector console.
  if (action.startsWith("connection.") || action.startsWith("integration_connection."))
    return "grape";
  if (action.startsWith("document.") || action.startsWith("file.")) return "cyan";
  if (action.startsWith("veeva_file.") || action.startsWith("container.")) return "cyan";
  // Phase 3 — projects + pipelines.
  if (action.startsWith("pipeline.") || action.startsWith("project.")) return "grape";
  if (action.startsWith("object.")) return "orange"; // pipeline write-back to Veeva
  return "brand";
}
